<template>
  <article :class="['card', `image-${figurePosition}`, `text-${textAlign}`, `${backgroundColor}`]">
    <figure v-if="$slots.figure">
      <slot name="figure"></slot>
    </figure>
    <div>
      <header v-if="heading">
        <component :is="headingTag" v-clean-html="heading" />
        <p v-if="subline" class="h5">
          {{ subline }}
        </p>
      </header>
      <div>
        <slot></slot>
      </div>
      <footer v-if="$slots.actions">
        <slot name="actions"></slot>
      </footer>
    </div>
  </article>
</template>


<script setup>
import { ColorTypes } from '../../../constants/enums'

const props = defineProps({
  heading: {
    type: String,
    default: null,
  },
  headingTag: {
    type: String,
    default: 'h3',
    validator: (value) => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', ''].includes(value),
  },
  subline: {
    type: String,
    default: null,
  },
  figurePosition: {
    type: String,
    default: 'top',
    validator: (value) => ['top', 'left', 'right', 'bottom', ''].includes(value),
  },
  textAlign: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'center', 'right', 'justify', ''].includes(value),
  },
  backgroundColor: {
    type: String,
    default: 'neutral-3',
    validator: (val) => ColorTypes.includes(val),
  },
})
</script>

<style scoped>
  .card {
    display: flex;
    flex: 1 1 0;  /* same size, if parent is a flex-container */
    max-width: var(--maxContainerWidth);
    padding: 1rem;
    gap: 1.5rem;
    margin-inline: auto;
  }

  .card:is(.image-top, .card.image-) {  /* if props.figurePosition is empty / default */
    flex-direction: column;
  }

  .card.image-bottom {
    flex-direction: column-reverse;
  }

  .card.image-right {
    flex-direction: row-reverse;
  }

  .card > figure {
    margin: 0;
    overflow: hidden;
    flex-shrink: 0;
  }

  .card:is(.image-right, .image-left) > figure {
    max-width: 50%;
  }

  .card:is(.image-top, .image-bottom, .image-) > figure {
    width: 100%;
  }

  .card > figure :is(img, video, svg) {
    height: 100%;
    width: 100%;
    max-height: 23rem;
    display: block;
    object-fit: cover;
    object-position: center;
  }

  .card:is(.image-right, .image-left) > figure :is(img, video, svg) {
    height: auto;
  }

  .card > div {
    flex-grow: 1;
    display: grid;
    gap: 1rem;
    grid-template-rows: auto 1fr auto;
    width: min(44rem, 80%);
    margin-inline: auto;
  }

  .card > div > header p {
    margin-block: 0;
    padding-inline: .3rem;
  }

  .card > div > div {
    padding-inline: .3rem;
  }

  .card > div > footer {
    flex-wrap: wrap;
    justify-content: v-bind('props.textAlign');
    display: flex;
    gap: 1rem;
    padding-bottom: 1.5rem;
  }


  @media screen and (width <= 576px) {
    .card > figure {
      margin: 0;
      overflow: hidden;
      flex-shrink: inherit;
    }

    .card > div {
      padding-top: 1rem;
    }
  }
</style>
